export const TRUSTED_OPERATOR_LABELS = {
  dcg: "Dream Car Giveaways",
  llf: "LLF Games",
  kilted: "Kilted Competitions",
};

export const TRUSTED_ACTIONS = [
  ["scan_enabled", "Scan"],
  ["apply_enabled", "Apply"],
  ["verify_enabled", "Verify"],
  ["publish_enabled", "Publish"],
  ["expire_enabled", "Expire"],
];

export function trustedOperatorControls(status, operatorKey) {
  return status?.trusted_operators?.[operatorKey] || {};
}

export function trustedNotificationsEnabled(status) {
  const controls = Object.values(status?.trusted_operators || {});
  if (controls.length === 0) return false;
  return controls.some((item) => item.notifications_enabled === true);
}

export function trustedPhaseLabel(status) {
  const controls = Object.values(status?.trusted_operators || {});
  if (controls.length === 0) return "Not configured";
  const scan = controls.some((item) => item.scan_enabled);
  const apply = controls.some((item) => item.apply_enabled);
  const verify = controls.some((item) => item.verify_enabled);
  const publish = controls.some((item) => item.publish_enabled);
  const expire = controls.some((item) => item.expire_enabled);
  if (scan && !apply && !verify && !publish && !expire) {
    return "Phase 1 - scan and notify only";
  }
  if (scan && apply && !verify && !publish) {
    return "Phase 2 - apply to review queue";
  }
  if (scan && apply && verify && !publish) {
    return "Phase 3 - verify only";
  }
  if (scan && apply && verify && publish) {
    return expire
      ? "Phase 4 - publish and expire"
      : "Phase 4 - publish only";
  }
  return scan ? "Custom trusted automation settings" : "Disabled";
}

export function trustedConfirmationCopy(
  status,
  operatorLabels = Object.values(TRUSTED_OPERATOR_LABELS),
  operatorKeys = null,
) {
  const phase = trustedPhaseLabel(status);
  const trusted = status?.trusted_operators || {};
  const controls = Array.isArray(operatorKeys) && operatorKeys.length > 0
    ? operatorKeys.map((key) => trusted[key]).filter(Boolean)
    : Object.values(trusted);
  const enabled = (field) => controls.some((item) => item[field]);
  const disabled = (field) => !enabled(field);
  const operators = operatorLabels.join(" and ");
  const parts = [
    `Trusted automation will run with the current backend settings for ${operators}.`,
    `${phase}.`,
    `Scan is ${enabled("scan_enabled") ? "enabled" : "disabled"}.`,
    `Apply is ${enabled("apply_enabled") ? "enabled" : "disabled"}.`,
    `Verify is ${enabled("verify_enabled") ? "enabled" : "disabled"}.`,
    `Publish is ${enabled("publish_enabled") ? "enabled" : "disabled"}.`,
    `Expire is ${enabled("expire_enabled") ? "enabled" : "disabled"}.`,
  ];
  if (
    enabled("scan_enabled")
    && disabled("apply_enabled")
    && disabled("verify_enabled")
    && disabled("publish_enabled")
    && disabled("expire_enabled")
  ) {
    parts.push("No public records should be changed.");
  }
  return parts.join(" ");
}

export function normaliseTrustedRunResult(result) {
  const summary = result?.summary || {};
  const errors = Number(summary.errors ?? result?.errors?.length ?? 0) || 0;
  return {
    operator_key: result?.operator_key || "",
    operator_name: result?.operator_name || TRUSTED_OPERATOR_LABELS[result?.operator_key] || "Unknown operator",
    status: result?.status || "unknown",
    dry_run: result?.dry_run,
    scanned: Number(summary.scanned ?? 0) || 0,
    created: Number(summary.created ?? 0) || 0,
    updated: Number(summary.updated ?? 0) || 0,
    auto_verified: Number(summary.auto_verified ?? 0) || 0,
    auto_published: Number(summary.auto_published ?? 0) || 0,
    review_needed: Number(summary.review_needed ?? 0) || 0,
    expired_hidden: Number(summary.expired_hidden ?? 0) || 0,
    errors,
    warnings: errors,
  };
}

export function aggregateTrustedRunResults(results = [], notificationsCreated = 0) {
  const normalised = results.map(normaliseTrustedRunResult);
  const totals = normalised.reduce((acc, item) => {
    for (const key of [
      "scanned", "created", "updated", "auto_verified",
      "auto_published", "review_needed", "expired_hidden", "errors", "warnings",
    ]) {
      acc[key] += item[key] || 0;
    }
    return acc;
  }, {
    scanned: 0,
    created: 0,
    updated: 0,
    auto_verified: 0,
    auto_published: 0,
    review_needed: 0,
    expired_hidden: 0,
    errors: 0,
    warnings: 0,
  });
  return {
    operators_run: normalised.map((item) => item.operator_name),
    results: normalised,
    totals,
    notifications_created: Math.max(0, Number(notificationsCreated) || 0),
  };
}
